import { lazy, Suspense, useLayoutEffect, type ComponentType } from 'react'
import {
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useRouteError,
} from 'react-router-dom'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { CookieConsentBanner } from '@/components/CookieConsentBanner'
import { resetWindowScroll } from '@/lib/resetWindowScroll'
import { diagNote } from '@/lib/reloadDiag'
import {
  goldGradientButtonSm,
  ctaRadius,
  outlineButton,
} from '@/lib/brandGradient'

/** A failed `import()` of a route chunk almost always means we just deployed
 *  and the user's tab is asking for filenames that no longer exist on the CDN.
 *  Recover by reloading once — the fresh index.html points at the new chunks.
 *  A short-lived sessionStorage guard prevents an infinite reload loop if the
 *  problem is something other than a stale deploy. */
const CHUNK_RELOAD_KEY = '__joms_chunk_reloaded_at'
const CHUNK_RELOAD_WINDOW_MS = 10_000

function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err)
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    msg.includes('error loading dynamically imported module')
  )
}

/** Number of silent in-tab retries before we fall back to a full reload. */
const CHUNK_IMPORT_RETRIES = 2
/** Backoff between silent retries — short, since the common case is iOS Safari
 *  aborting an in-flight module fetch when the user taps/interacts mid-load. */
const CHUNK_RETRY_DELAY_MS = 200

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

function lazyWithReload<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    // Transient chunk fetches (notably iOS Safari aborting an in-flight module
    // when the user taps/interacts mid-load) usually succeed on a quick retry.
    // Retry silently first so the user never sees a page refresh; only fall back
    // to the reload-once recovery below when every retry fails (stale deploy).
    for (let attempt = 0; attempt <= CHUNK_IMPORT_RETRIES; attempt++) {
      try {
        return await factory()
      } catch (err) {
        if (typeof window === 'undefined' || !isChunkLoadError(err)) throw err
        if (attempt < CHUNK_IMPORT_RETRIES) {
          await delay(CHUNK_RETRY_DELAY_MS * (attempt + 1))
          continue
        }
        // Retries exhausted: the chunk is genuinely gone (almost always a stale
        // deploy). Reload once — guarded so we never loop — to fetch fresh HTML.
        const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0)
        if (
          Number.isFinite(last) &&
          Date.now() - last < CHUNK_RELOAD_WINDOW_MS
        ) {
          throw err
        }
        sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
        diagNote(
          `chunk-guard reload: ${err instanceof Error ? err.message : String(err)}`,
        )
        window.location.reload()
        return new Promise<{ default: T }>(() => {})
      }
    }
    // Unreachable: the loop either returns the module or throws/reloads above.
    throw new Error('lazyWithReload: exhausted retries without resolution')
  })
}

/** Router-level fallback. Chunk errors are auto-recovered by `lazyWithReload`;
 *  this catches anything else (route loader throws, ProtectedRoute crashes,
 *  etc.) and shows a branded recover-or-go-home affordance. */
const RouteErrorElement = () => {
  const error = useRouteError()
  const message =
    error instanceof Error
      ? error.message
      : 'Something went wrong loading this page.'
  return (
    <div className="mx-auto mt-16 max-w-md rounded-2xl border border-white/10 bg-black/[0.04] p-6 text-center dark:bg-white/[0.04]">
      <h2 className="text-lg font-semibold">Something went wrong</h2>
      <p className="mt-2 text-sm text-white/60">{message}</p>
      <div className="mt-5 flex justify-center gap-2">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className={`px-4 py-2 text-sm font-semibold text-[#1a1a1a] ${goldGradientButtonSm}`}
        >
          Reload
        </button>
        <a
          href="/"
          className={`border border-white/15 px-4 py-2 text-sm font-semibold ${ctaRadius} ${outlineButton}`}
        >
          Home
        </a>
      </div>
    </div>
  )
}

/** Centered brand-gold spinner shown while a lazy-loaded route's chunk is
 *  being fetched. Replaces the previous "Loading page…" text in the corner —
 *  full-viewport so the affordance reads as the page is loading, not as a
 *  stray snippet of text. */
const RouteFallback = () => (
  <div
    role="status"
    aria-live="polite"
    aria-label="Loading page"
    className="flex min-h-[60vh] w-full items-center justify-center"
  >
    <div className="flex flex-col items-center gap-3">
      <span
        aria-hidden
        className="h-9 w-9 animate-spin rounded-full border-2 border-[#F6CB86]/25 border-t-[#F6CB86]"
      />
      <span className="text-sm text-white/55">Loading…</span>
    </div>
  </div>
)

import HomePage from '@/pages/HomePage'
const LoginPage = lazyWithReload(() => import('@/pages/LoginPage'))
const OnboardingPage = lazyWithReload(() => import('@/pages/OnboardingPage'))
const LaunchpadPage = lazyWithReload(() => import('@/pages/LaunchpadPage'))
const LeaderboardPage = lazyWithReload(() => import('@/pages/LeaderboardPage'))
const DashboardPage = lazyWithReload(() => import('@/pages/DashboardPage'))
const ValidateIdeasPage = lazyWithReload(
  () => import('@/pages/ValidateIdeasPage'),
)
const ValidateIdeaDetailPage = lazyWithReload(
  () => import('@/pages/ValidateIdeaDetailPage'),
)
const ExpertValidationRequestPage = lazyWithReload(
  () => import('@/pages/ExpertValidationRequestPage'),
)
const ExpertValidationMarketplacePage = lazyWithReload(
  () => import('@/pages/ExpertValidationMarketplacePage'),
)
const ExpertValidationDashboardPage = lazyWithReload(
  () => import('@/pages/ExpertValidationDashboardPage'),
)
const SubmitIdeaPage = lazyWithReload(() => import('@/pages/SubmitIdeaPage'))
const IdeaReportPage = lazyWithReload(() => import('@/pages/IdeaReportPage'))
const ReportReviewPage = lazyWithReload(
  () => import('@/pages/ReportReviewPage'),
)
const PublicProfilePage = lazyWithReload(
  () => import('@/pages/PublicProfilePage'),
)
const EditProfilePage = lazyWithReload(() => import('@/pages/EditProfilePage'))
const ScoringPage = lazyWithReload(() => import('@/pages/ScoringPage'))
const PrivacyPolicyPage = lazyWithReload(
  () => import('@/pages/legal/PrivacyPolicyPage'),
)
const TermsOfServicePage = lazyWithReload(
  () => import('@/pages/legal/TermsOfServicePage'),
)
const CookiePolicyPage = lazyWithReload(
  () => import('@/pages/legal/CookiePolicyPage'),
)
const AboutPage = lazyWithReload(() => import('@/pages/AboutPage'))
const HelpCenterPage = lazyWithReload(() => import('@/pages/HelpCenterPage'))

/**
 * Group tab-like routes so list ↔ detail hops do not replay the crossfade.
 * Top-level hops (home → explore, etc.) still animate.
 */
function routeTransitionKey(pathname: string): string {
  const root = pathname.split('/').filter(Boolean)[0]
  if (root === 'validate-ideas') return '/validate-ideas'
  if (root === 'dashboard') return '/dashboard'
  return pathname
}

const RootLayout = () => {
  const location = useLocation()

  // Reset scroll before the next route paints — unless the URL carries a hash
  // (e.g. footer "Testimonials" → /#testimonials-heading), in which case scroll
  // to that section. The target may belong to a lazy-loaded page that hasn't
  // mounted yet, so retry across a few frames before giving up.
  useLayoutEffect(() => {
    const id = location.hash ? decodeURIComponent(location.hash.slice(1)) : ''
    if (!id) {
      resetWindowScroll()
      return
    }
    let raf = 0
    let attempts = 0
    const tryScroll = () => {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        return
      }
      if (attempts++ < 30) raf = requestAnimationFrame(tryScroll)
    }
    raf = requestAnimationFrame(tryScroll)
    return () => cancelAnimationFrame(raf)
  }, [location.pathname, location.key, location.hash])

  return (
    <>
      <div
        key={routeTransitionKey(location.pathname)}
        className="page-route-enter w-full min-w-0 overflow-x-hidden"
      >
        <Outlet />
      </div>
      {/* Banner lives inside the router so its <Link>s have Router context. */}
      <CookieConsentBanner />
    </>
  )
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteErrorElement />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: '/login',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: '/signup',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <OnboardingPage />
          </Suspense>
        ),
      },
      {
        path: '/launchpad',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <ProtectedRoute>
              <LaunchpadPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/leaderboard',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <LeaderboardPage />
          </Suspense>
        ),
      },
      {
        path: '/dashboard',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/dashboard/ideas/:ideaId/report',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <ProtectedRoute>
              <IdeaReportPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/report-review',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <ProtectedRoute>
              <ReportReviewPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/submit-idea',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <ProtectedRoute>
              <SubmitIdeaPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/validate-ideas',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <ValidateIdeasPage />
          </Suspense>
        ),
      },
      {
        path: '/validate-ideas/:ideaId',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <ValidateIdeaDetailPage />
          </Suspense>
        ),
      },
      {
        path: '/validate/experts/request/:ideaId',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <ProtectedRoute>
              <ExpertValidationRequestPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/validate/experts',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <ExpertValidationMarketplacePage />
          </Suspense>
        ),
      },
      {
        /** Public marketing preview — same UI as dashboard; no login required. */
        path: '/validate/experts/workflow-preview',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <ExpertValidationDashboardPage />
          </Suspense>
        ),
      },
      {
        path: '/validate/experts/dashboard',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <ProtectedRoute>
              <ExpertValidationDashboardPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/users/:handle',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <PublicProfilePage />
          </Suspense>
        ),
      },
      {
        path: '/profile/edit',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <ProtectedRoute>
              <EditProfilePage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/scoring',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <ScoringPage />
          </Suspense>
        ),
      },
      {
        path: '/privacy',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <PrivacyPolicyPage />
          </Suspense>
        ),
      },
      {
        path: '/terms',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <TermsOfServicePage />
          </Suspense>
        ),
      },
      {
        path: '/cookies',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <CookiePolicyPage />
          </Suspense>
        ),
      },
      {
        path: '/about',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <AboutPage />
          </Suspense>
        ),
      },
      {
        path: '/help-center',
        element: (
          <Suspense fallback={<RouteFallback />}>
            <HelpCenterPage />
          </Suspense>
        ),
      },
    ],
  },
])

export const AppRouter = () => <RouterProvider router={router} />
